import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { Card } from '../components/Card';
import { FixtureCard } from '../components/FixtureCard';
import { api } from '../lib/api';
import { haptic } from '../lib/telegram';
import { cn } from '../lib/utils';

function ukDayShort(d: Date): string {
  return d.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase().slice(0, 3);
}

function dayLabel(d: Date): string {
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

// Pill row: yesterday, today, tomorrow, + up to +13 days
function buildPillRange(): Date[] {
  const out: Date[] = [];
  const start = new Date();
  start.setUTCHours(0, 0, 0, 0);
  for (let offset = -1; offset <= 13; offset++) {
    const d = new Date(start);
    d.setUTCDate(d.getUTCDate() + offset);
    out.push(d);
  }
  return out;
}

export function Today() {
  const upcoming = useQuery({
    queryKey: ['fixtures', 'upcoming', 14],
    queryFn: () => api.fixturesUpcoming(14),
  });

  const todayKey = new Date().toISOString().slice(0, 10);
  const [selected, setSelected] = useState<string>(todayKey);
  const [showAll, setShowAll] = useState(false);
  const pillRange = useMemo(() => buildPillRange(), []);
  const TOP_COUNT = 3;

  // Group fixtures by UTC date key
  const byDay = useMemo(() => {
    const fixtures = upcoming.data?.fixtures ?? [];
    const days = new Map<string, typeof fixtures>();
    for (const f of fixtures) {
      const key = new Date(f.kickoffAt * 1000).toISOString().slice(0, 10);
      const arr = days.get(key) ?? [];
      arr.push(f);
      days.set(key, arr);
    }
    return days;
  }, [upcoming.data]);

  const selectedFixtures = byDay.get(selected) ?? [];
  const pillsRef = useRef<HTMLDivElement>(null);

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <header className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl uppercase tracking-wide">Fixtures</h1>
        <span className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
          {selected === todayKey ? 'Today' : dayLabel(new Date(selected + 'T00:00:00Z'))}
        </span>
      </header>

      {/* Sticky pill row */}
      <div
        ref={pillsRef}
        className="sticky top-0 -mx-4 px-4 py-2 bg-bg-night/95 backdrop-blur-sm z-10 overflow-x-auto"
        style={{ scrollbarWidth: 'none' }}
      >
        <div className="flex gap-1.5 min-w-max">
          {pillRange.map((d) => {
            const key = d.toISOString().slice(0, 10);
            const isToday = key === todayKey;
            const isSelected = key === selected;
            const count = byDay.get(key)?.length ?? 0;
            return (
              <button
                key={key}
                onClick={() => {
                  haptic.select();
                  setSelected(key);
                  setShowAll(false);
                }}
                className={cn(
                  'flex flex-col items-center justify-center rounded-lg px-3 py-1.5 min-w-[56px]',
                  'border transition-colors',
                  isSelected
                    ? 'bg-trophy-gold border-trophy-gold text-bg-night'
                    : isToday
                      ? 'bg-bg-card border-trophy-gold/40 text-trophy-gold'
                      : 'bg-bg-card border-border-base text-text-dim',
                )}
              >
                <span className="font-display-alt text-[10px] tracking-widest">
                  {isToday ? 'TODAY' : ukDayShort(d)}
                </span>
                <span className="font-mono text-sm font-bold tabular-nums">
                  {d.getUTCDate()}
                </span>
                {count > 0 && (
                  <span
                    className={cn(
                      'font-mono text-[9px]',
                      isSelected ? 'text-bg-night/60' : 'text-text-faint',
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {upcoming.isLoading && <Skeleton />}

      {!upcoming.isLoading && selectedFixtures.length === 0 && (
        <Card>
          <div className="text-text-dim text-sm">
            No fixtures on this day. Try another date.
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {(showAll ? selectedFixtures : selectedFixtures.slice(0, TOP_COUNT)).map((f) => (
          <FixtureCard key={f.id} fixture={f} />
        ))}
        {!showAll && selectedFixtures.length > TOP_COUNT && (
          <button
            onClick={() => {
              haptic.tap();
              setShowAll(true);
            }}
            className="w-full rounded-xl border border-border-base bg-bg-card py-3 font-display-alt text-xs uppercase tracking-widest text-trophy-gold active:scale-[0.99] transition-transform"
          >
            See all {selectedFixtures.length} matches →
          </button>
        )}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-20 rounded-xl bg-bg-card border border-border-base" />
      ))}
    </div>
  );
}
